<template>
    <TooltipProvider>
        <TooltipRoot v-model:open="open" :delay-duration="delayDuration" :disable-closing-trigger="openOnClick" :disabled="disabled">
            <TooltipTrigger v-bind="$attrs" :as-child="asChild" :aria-label="triggerAriaLabel" @click="handleTriggerClick">
                <slot name="trigger"></slot>
            </TooltipTrigger>
            <TooltipPortal>
                <TooltipContent
                    :side="side"
                    :side-offset="sideOffset"
                    :avoid-collisions="true"
                    :align="align"
                    :align-offset="alignOffset"
                    :class="[classes.content, theme === 'dark' ? classes.contentDark : classes.contentLight, contentClass]">
                    <slot name="content"></slot>
                    <TooltipArrow v-if="showArrow" :class="[classes.arrow, theme === 'dark' ? classes.arrowDark : classes.arrowLight, arrowClass]" :height="8" />
                </TooltipContent>
            </TooltipPortal>
        </TooltipRoot>
    </TooltipProvider>
</template>

<script setup lang="ts">
import { TooltipArrow, TooltipContent, type TooltipContentProps, TooltipPortal, TooltipProvider, TooltipRoot, TooltipTrigger } from "reka-ui";

// TooltipRoot is renderless, so attrs (and listeners) bound to it are dropped. Sending them to the
// trigger lets an outer asChild trigger, e.g. DropdownMenuTrigger, fuse onto the same element.
defineOptions({ inheritAttrs: false });

type BaseTooltipProps = Partial<Pick<TooltipContentProps, "align" | "alignOffset" | "side" | "sideOffset">> & {
    delayDuration?: number;
    disabled?: boolean;
    asChild?: boolean;
    // does not use :deep() since the content is portaled
    contentClass?: string;
    arrowClass?: string;
    triggerAriaLabel?: string;
    showArrow?: boolean;
    openOnClick?: boolean;
    /** TODO: Remove after the tonal_theme rollout; tonal collapses both values to the same tooltip. */
    theme?: "light" | "dark";
};

const {
    delayDuration = 0,
    side = "top",
    sideOffset = 0,
    align = "center",
    alignOffset = 0,
    asChild = true,
    showArrow = true,
    openOnClick = false,
    disabled = false,
    theme = "light",
} = defineProps<BaseTooltipProps>();

const open = defineModel<boolean>("open");

function handleTriggerClick() {
    if (openOnClick) {
        open.value = true;
    }
}
</script>

<style module="classes">
@reference "../../../css/tailwind.css";
/* TooltipArrow class is bound to a child element and you cannot access its containing span, so we use :has() selector from a parent element */
.content {
    &[data-side="top"] span:has(svg.arrow) {
        bottom: 2px !important;
    }

    &[data-side="bottom"] span:has(svg.arrow) {
        top: 2px !important;
    }

    &[data-side="right"] span:has(svg.arrow) {
        left: 2px !important;
    }

    &[data-side="left"] span:has(svg.arrow) {
        right: 2px !important;
    }
}

@layer components {
    .content {
        @apply rounded-lg z-50;
    }

    .contentLight {
        @apply px-2 py-1 bg-tooltip-light-bg text-tooltip-light-text details font-medium border-2 border-tooltip-light-border;
    }

    .contentDark {
        @apply bg-tooltip-dark-bg text-tooltip-dark-text scaffold-details-bold rounded-md px-2.25 py-1.5;
    }

    :global(html[data-theme="tonal"]) .contentLight,
    :global(html[data-theme="tonal"]) .contentDark {
        @apply px-3.5 py-1.5 scaffold-details-small rounded-md;
    }

    :global(html[data-theme="tonal"]) .contentLight {
        @apply border-0;
    }

    .arrowLight {
        @apply fill-tooltip-light-bg stroke-tooltip-light-border stroke-2;
    }

    .arrowDark {
        @apply fill-tooltip-dark-bg;
    }

    :global(html[data-theme="tonal"]) .arrowLight {
        @apply stroke-0;
    }
}
</style>
